import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, LayoutGrid, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import API from '@/config/api';

/**
 * 7-day classroom × day heatmap showing staff coverage vs DCF required ratio.
 * Cells are color-coded green (compliant) / red (under-staffed) / gray (no roster).
 * Click a cell to see the exact counts.
 */
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  // Sunday = 0 → back 6, Monday = 1 → back 0
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date.toISOString().split('T')[0];
};

const addDays = (iso, n) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

const formatMonDay = (iso) => {
  const [_y, m, dd] = iso.split('-');
  return `${parseInt(m, 10)}/${parseInt(dd, 10)}`;
};

const RatioHeatmapWeek = () => {
  const { getAuthHeaders } = useAuth();
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/classrooms/ratios/weekly?week_start=${weekStart}`,
        getAuthHeaders(),
      );
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [weekStart, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const shiftWeek = (delta) => setWeekStart(addDays(weekStart, delta * 7));

  // Summary: red cells count
  const gaps = (data?.classrooms || []).flatMap(c =>
    c.days.filter(day => !day.is_compliant && c.rostered_children > 0)
  ).length;

  return (
    <Card className="card-clay" data-testid="ratio-heatmap-card">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <LayoutGrid className="w-5 h-5 text-primary" />
              Weekly Ratio Heatmap
            </CardTitle>
            <CardDescription>
              Coverage gaps across all classrooms for the selected week.{' '}
              {gaps > 0
                ? <span className="text-red-600 font-medium">{gaps} slot{gaps === 1 ? '' : 's'} under-staffed.</span>
                : <span className="text-green-600 font-medium">All rostered classrooms covered.</span>}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm" variant="outline"
              onClick={() => shiftWeek(-1)}
              data-testid="heatmap-prev-week"
              className="h-8"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium px-2 min-w-[140px] text-center" data-testid="heatmap-week-label">
              Week of {formatMonDay(weekStart)}
            </span>
            <Button
              size="sm" variant="outline"
              onClick={() => shiftWeek(1)}
              data-testid="heatmap-next-week"
              className="h-8"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => setWeekStart(getMonday(new Date()))}
              data-testid="heatmap-this-week"
              className="h-8 text-xs"
            >
              This week
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && data && (
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-1 text-sm">
              <thead>
                <tr>
                  <th className="text-left font-medium text-muted-foreground pb-2 pr-2 min-w-[140px]">Classroom</th>
                  {data.days.map((d, i) => (
                    <th key={d} className="text-center font-medium text-muted-foreground pb-2 px-1">
                      <div className="text-xs">{WEEKDAY_LABELS[i]}</div>
                      <div className="text-[11px] text-muted-foreground/70">{formatMonDay(d)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.classrooms.map((c) => (
                  <tr key={c.classroom_id}>
                    <td className="pr-2">
                      <div className="font-medium">{c.classroom_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.rostered_children} kids · DCF {c.dcf_primary_ratio}
                      </div>
                    </td>
                    {c.days.map((day) => {
                      const noRoster = c.rostered_children === 0;
                      const bg = noRoster
                        ? 'bg-slate-100 text-slate-400'
                        : day.is_compliant
                          ? 'bg-green-100 text-green-700 border border-green-200'
                          : 'bg-red-100 text-red-700 border border-red-300';
                      const title = noRoster
                        ? `No children rostered`
                        : day.is_compliant
                          ? `${day.staff_count}/${day.required_staff} staff — Compliant`
                          : `${day.staff_count}/${day.required_staff} staff — Need ${day.staff_needed} more`;
                      return (
                        <td key={day.date} className="text-center">
                          <div
                            className={`${bg} rounded-md py-2 px-1 text-xs font-semibold cursor-help select-none`}
                            title={title}
                            data-testid={`heatmap-cell-${c.classroom_id}-${day.date}`}
                          >
                            {noRoster
                              ? '—'
                              : `${day.staff_count}/${day.required_staff}`}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-sm bg-green-100 border border-green-200" />
                Compliant
              </div>
              <div className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-sm bg-red-100 border border-red-300" />
                Under-staffed
              </div>
              <div className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-sm bg-slate-100" />
                No roster
              </div>
              <div className="ml-auto">Cells show: <span className="font-mono">scheduled / required</span></div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RatioHeatmapWeek;
